import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MaintenanceWindow, Project, ProjectComponent, StatusPageSettings } from 'src/app/models/project.model';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { ToastService } from 'src/app/services/toast.service';
import { MembershipService } from 'src/app/services/membership.service';
import { AuthService } from 'src/app/services/auth.service';
import { Team } from 'src/app/models/team.model';
import { environment } from 'src/environments/environment';

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const LOGO_MAX_BYTES = 2 * 1024 * 1024;

@Component({
  selector: 'app-project-editor',
  templateUrl: './project-editor.component.html',
  styleUrls: ['./project-editor.component.scss'],
  standalone: false
})
export class ProjectEditorPageComponent implements OnInit {

  project: Project = { name: '' };
  projectId: string = null;
  isEditMode = false;
  showDeleteModal = false;
  logoBusy = false;
  logoError = '';
  logoDragOver = false;

  components: ProjectComponent[] = [];
  newComponentName = '';
  statusPage: StatusPageSettings = { slug: '', title: '' };
  slugError = '';

  expandedComponents = new Set<number>();

  // maintenance window state per component index
  maintenanceWindows: { [componentId: number]: MaintenanceWindow[] } = {};
  showMaintenanceFor = new Set<number>();
  newWindow: { [componentId: number]: Partial<MaintenanceWindow> } = {};

  // One window applied to several components at once. Windows stay per component —
  // this only avoids retyping the same times N times, which is where they drift apart.
  showFanout = false;
  fanoutComponentIds = new Set<number>();
  fanoutWindow: Partial<MaintenanceWindow> = { title: '', message: '', start_at: '', end_at: '' };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private projectService: ProjectManagementService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef,
    private membership: MembershipService,
    private auth: AuthService,
  ) { }

  /** Teams the new project can go into: your own teams (all teams for the owner and admins). */
  teams: Team[] = [];
  selectedTeams = new Set<string>();
  teamError = '';
  seesAllProjects = ['owner', 'admin'].includes(this.auth.orgRole());

  toggleTeam(uuid: string) {
    this.selectedTeams.has(uuid) ? this.selectedTeams.delete(uuid) : this.selectedTeams.add(uuid);
    this.teamError = '';
  }

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');
    this.isEditMode = !!this.projectId;

    if (this.isEditMode) {
      [this.project, this.components, this.statusPage] = await Promise.all([
        this.projectService.getProject(this.projectId).toPromise(),
        this.projectService.getComponents(this.projectId).toPromise(),
        this.projectService.getStatusPage(this.projectId).toPromise(),
      ]);
      this.cdr.markForCheck();
    } else {
      const [teams, role] = await Promise.all([
        this.membership.getTeams().toPromise(),
        this.membership.myRole().toPromise(),
      ]);
      this.teams = teams ?? [];
      this.seesAllProjects = role === 'owner' || role === 'admin';
      // A member with a single team creates into it; nothing to choose.
      if (!this.seesAllProjects && this.teams.length === 1) this.selectedTeams.add(this.teams[0].uuid);
      this.cdr.markForCheck();
    }
  }

  async onSave() {
    try {
      if (this.isEditMode) {
        await Promise.all([
          this.projectService.updateProject(this.projectId, { name: this.project.name }).toPromise(),
          this.projectService.updateStatusPage(this.projectId, { slug: this.statusPage.slug, title: this.statusPage.title }).toPromise(),
        ]);
        this.toast.show('Project updated successfully', 'success');
        this.router.navigate(['/main', 'project', this.projectId]);
      } else {
        await this.projectService.createProject({
          name: this.project.name,
          components: this.components,
          team_uuids: [...this.selectedTeams],
        }).toPromise();
        this.toast.show('Project created successfully', 'success');
        this.router.navigate(['/main', 'dashboard']);
      }
    } catch (err: any) {
      const slugMsg = err?.error?.slug?.[0];
      const teamMsg = err?.team_uuids?.[0] ?? err?.error?.team_uuids?.[0];
      if (teamMsg) {
        this.teamError = teamMsg;
        this.cdr.markForCheck();
      } else if (slugMsg) {
        this.slugError = slugMsg;
      } else {
        this.toast.show(this.isEditMode ? 'Failed to update project' : 'Failed to create project', 'error');
      }
    }
  }

  async onDelete() {
    try {
      await this.projectService.deleteProject(this.projectId).toPromise();
      this.toast.show('Project deleted', 'success');
      this.router.navigate(['/main', 'dashboard']);
    } catch {
      this.toast.show('Failed to delete project', 'error');
    }
  }

  get logoSrc(): string {
    return `${environment.baseUrl}${this.project.logo_url}`;
  }

  async onLogoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';  // picking the same file again should upload again
    if (file) await this.uploadLogo(file);
  }

  onLogoDragOver(event: DragEvent) {
    if (this.logoBusy || !event.dataTransfer?.types.includes('Files')) return;
    event.preventDefault();  // allows the drop
    event.dataTransfer.dropEffect = 'copy';
    this.logoDragOver = true;
  }

  onLogoDragLeave(event: DragEvent) {
    // dragleave also fires when moving onto a child element; only reset when leaving the zone.
    const zone = event.currentTarget as HTMLElement;
    if (!zone.contains(event.relatedTarget as Node)) this.logoDragOver = false;
  }

  async onLogoDrop(event: DragEvent) {
    event.preventDefault();
    this.logoDragOver = false;
    const file = event.dataTransfer?.files?.[0];
    if (file && !this.logoBusy) await this.uploadLogo(file);
  }

  private async uploadLogo(file: File) {
    // The file picker filters by `accept`, dropped files don't.
    if (!LOGO_TYPES.includes(file.type)) {
      this.logoError = 'Please use a PNG, JPEG, WebP or GIF image.';
      this.cdr.markForCheck();
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      this.logoError = 'The logo must be 2 MB or smaller.';
      this.cdr.markForCheck();
      return;
    }

    this.logoBusy = true;
    this.logoError = '';
    this.cdr.markForCheck();
    try {
      const { logo_url } = await this.projectService.uploadProjectLogo(this.projectId, file).toPromise();
      this.project = { ...this.project, logo_url };
      this.toast.show('Logo updated', 'success');
    } catch (err: any) {
      // The ErrorInterceptor rethrows the response body itself.
      this.logoError = err?.file?.[0] ?? err?.error?.file?.[0] ?? 'Failed to upload the logo';
    } finally {
      this.logoBusy = false;
      this.cdr.markForCheck();
    }
  }

  async removeLogo() {
    this.logoBusy = true;
    this.cdr.markForCheck();
    try {
      await this.projectService.deleteProjectLogo(this.projectId).toPromise();
      this.project = { ...this.project, logo_url: null };
      this.toast.show('Logo removed', 'success');
    } catch {
      this.toast.show('Failed to remove the logo', 'error');
    } finally {
      this.logoBusy = false;
      this.cdr.markForCheck();
    }
  }

  toggleComponent(index: number) {
    if (this.expandedComponents.has(index)) {
      this.expandedComponents.delete(index);
    } else {
      this.expandedComponents.add(index);
    }
  }

  addComponent() {
    const name = this.newComponentName.trim();
    if (!name) return;

    if (this.isEditMode) {
      this.projectService.addComponent(this.projectId, { name }).toPromise()
        .then(component => {
          this.components.push(component);
          this.expandedComponents.add(this.components.length - 1);
          this.newComponentName = '';
          this.toast.show('Component added', 'success');
        })
        .catch(() => this.toast.show('Failed to add component', 'error'));
    } else {
      this.components.push({ name, check_type: 'HTTP', check_interval_seconds: 60 });
      this.expandedComponents.add(this.components.length - 1);
      this.newComponentName = '';
    }
  }

  async removeComponent(component: ProjectComponent, index: number) {
    if (this.isEditMode && component.id) {
      try {
        await this.projectService.deleteComponent(this.projectId, component.id).toPromise();
        this.components.splice(index, 1);
        this._shiftExpandedAfterRemoval(index);
        this.toast.show('Component removed', 'success');
      } catch {
        this.toast.show('Failed to remove component', 'error');
      }
    } else {
      this.components.splice(index, 1);
      this._shiftExpandedAfterRemoval(index);
    }
  }

  async saveComponent(component: ProjectComponent) {
    if (!this.isEditMode || !component.id) return;
    try {
      await this.projectService.updateComponent(this.projectId, component.id, {
        name: component.name,
        description: component.description,
        check_type: component.check_type,
        check_url: component.check_url,
        check_interval_seconds: component.check_interval_seconds,
      }).toPromise();
      this.toast.show('Component saved', 'success');
    } catch {
      this.toast.show('Failed to save component', 'error');
    }
  }

  // ── Maintenance windows ──────────────────────────────────────────────────

  async toggleMaintenance(component: ProjectComponent) {
    if (!component.id) return;
    if (this.showMaintenanceFor.has(component.id)) {
      this.showMaintenanceFor.delete(component.id);
      return;
    }
    this.showMaintenanceFor.add(component.id);
    if (!this.maintenanceWindows[component.id]) {
      await this.loadMaintenanceWindows(component);
    }
    if (!this.newWindow[component.id]) {
      this.newWindow[component.id] = { title: '', message: '', start_at: '', end_at: '' };
    }
    this.cdr.markForCheck();
  }

  async loadMaintenanceWindows(component: ProjectComponent) {
    try {
      this.maintenanceWindows[component.id] = await this.projectService
        .getMaintenanceWindows(this.projectId, component.id).toPromise();
    } catch {
      this.toast.show('Failed to load maintenance windows', 'error');
    }
  }

  async addMaintenanceWindow(component: ProjectComponent) {
    if (!component.id) return;
    const data = this.newWindow[component.id];
    if (!data?.start_at || !data?.end_at) {
      this.toast.show('Start and end time are required', 'error');
      return;
    }
    try {
      const created = await this.projectService
        .createMaintenanceWindow(this.projectId, component.id, data).toPromise();
      this.maintenanceWindows[component.id] = [
        ...(this.maintenanceWindows[component.id] || []),
        created,
      ];
      this.newWindow[component.id] = { title: '', message: '', start_at: '', end_at: '' };
      this.toast.show('Maintenance window added', 'success');
      this.cdr.markForCheck();
    } catch {
      this.toast.show('Failed to add maintenance window', 'error');
    }
  }

  toggleFanoutComponent(component: ProjectComponent) {
    if (!component.id) return;
    if (this.fanoutComponentIds.has(component.id)) {
      this.fanoutComponentIds.delete(component.id);
    } else {
      this.fanoutComponentIds.add(component.id);
    }
  }

  async addMaintenanceWindowForComponents() {
    const componentIds = [...this.fanoutComponentIds];
    if (componentIds.length === 0) {
      this.toast.show('Pick at least one component', 'error');
      return;
    }
    if (!this.fanoutWindow.start_at || !this.fanoutWindow.end_at) {
      this.toast.show('Start and end time are required', 'error');
      return;
    }
    try {
      const created = await this.projectService.createMaintenanceWindowForComponents(this.projectId, {
        component_ids: componentIds,
        title: this.fanoutWindow.title,
        message: this.fanoutWindow.message,
        start_at: this.fanoutWindow.start_at,
        end_at: this.fanoutWindow.end_at,
      }).toPromise();

      // Fold the new windows into whichever per-component lists are already loaded, so
      // the panels below do not have to be reopened to show them.
      for (const window of created || []) {
        if (window.component && this.maintenanceWindows[window.component]) {
          this.maintenanceWindows[window.component] = [...this.maintenanceWindows[window.component], window];
        }
      }
      this.fanoutWindow = { title: '', message: '', start_at: '', end_at: '' };
      this.fanoutComponentIds.clear();
      this.toast.show(`Maintenance scheduled on ${created?.length ?? 0} components`, 'success');
      this.cdr.markForCheck();
    } catch {
      this.toast.show('Failed to schedule maintenance', 'error');
    }
  }

  async deleteMaintenanceWindow(component: ProjectComponent, windowId: number) {
    if (!component.id) return;
    try {
      await this.projectService
        .deleteMaintenanceWindow(this.projectId, component.id, windowId).toPromise();
      this.maintenanceWindows[component.id] = this.maintenanceWindows[component.id]
        .filter(w => w.id !== windowId);
      this.toast.show('Maintenance window removed', 'success');
      this.cdr.markForCheck();
    } catch {
      this.toast.show('Failed to remove maintenance window', 'error');
    }
  }

  private _shiftExpandedAfterRemoval(removedIndex: number) {
    const updated = new Set<number>();
    this.expandedComponents.forEach(i => {
      if (i < removedIndex) updated.add(i);
      else if (i > removedIndex) updated.add(i - 1);
    });
    this.expandedComponents = updated;
  }
}
